/**
 * Design system component: ToggleButtonGroupOptions
 */

import { memo } from "react";
import MuiToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import type { ToggleButtonGroupProps as MuiToggleButtonGroupProps } from "@mui/material/ToggleButtonGroup";
import ToggleButton, { ToggleButtonProps } from "components/ToggleButton/ToggleButton";
import { FormHelperText } from "@mui/material";
import Tooltip from "components/TooltipV2";
import type { TooltipProps } from "components/TooltipV2/Tooltip";
import Span from "components/primitives/Span";

export interface ToggleButtonOptionsProps extends ToggleButtonProps {
  label: string;
  value: string;
}

interface OptionWithTooltip extends ToggleButtonOptionsProps {
  slotProps?: {
    tooltip?: Omit<TooltipProps, "children" | "placement">;
  };
}

interface ToggleButtonGroupProps extends MuiToggleButtonGroupProps {
  options: Array<OptionWithTooltip>;
  size?: "small" | "medium" | "large";
  error?: boolean;
  helperText?: React.ReactNode;
}

/**
 * @name ToggleButtonGroupOptions
 * @description
 * ToggleButtonGroup component with options to easily create a group of toggle buttons.
 *
 * @param options Array of objects with label and value properties.
 *
 */
function ToggleButtonGroupOptions({ options, ...props }: ToggleButtonGroupProps) {
  const { size, error, helperText, ...rest } = props;

  return (
    <MuiToggleButtonGroup {...rest} color={error ? "error" : rest.color}>
      {options.map(({ value, label, slotProps, ...restOfOptions }) => (
        <Tooltip key={value} title={null} {...slotProps?.tooltip} placement="top">
          <Span sx={{ display: "inline-flex", flex: rest.fullWidth ? 1 : undefined }}>
            <ToggleButton
              id={restOfOptions.id}
              size={size}
              name={value}
              color="secondary"
              {...restOfOptions}
              value={value}
            >
              {label}
            </ToggleButton>
          </Span>
        </Tooltip>
      ))}
      {helperText && <FormHelperText error={error}>{helperText}</FormHelperText>}
    </MuiToggleButtonGroup>
  );
}

export default memo(ToggleButtonGroupOptions);
